import React, { ChangeEvent, DragEvent, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowDown } from '@fortawesome/pro-duotone-svg-icons';
import { useSnackbar } from 'notistack';
import { useHandleDropConstraints } from './useHandleDropConstraints';

export type FileUploadProps = {
    accept: string;
    formatInfo: string;
    width?: string;
    height?: string;
    maxMbSize?: number;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onDrop: (event: DragEvent<HTMLElement>) => void;
    inline?: boolean;
    disableFormat?: boolean;
    border?: boolean;
    hoverBorder?: boolean;
    btnText?: string;
    dropText?: string;
    additionalInfo?: React.ReactNode;
};

export const FileUpload: React.FunctionComponent<FileUploadProps> = function ({
    accept,
    formatInfo,
    width = '100%',
    height = '214px',
    inline = false,
    onChange,
    onDrop,
    maxMbSize,
    hoverBorder = true,
    border = true,
    btnText = 'Cliquez ici',
    dropText = 'ou déposez vos fichiers',
    disableFormat = false,
    additionalInfo = null,
}) {
    const [isHover, setIsHover] = useState<number>(0);

    const stopDefaults = (e: DragEvent | MouseEvent) => {
        if (hoverBorder) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    const { enqueueSnackbar } = useSnackbar();

    const { handleDropConstraints } = useHandleDropConstraints({
        accept,
        maxMbSize,
        onDrop,
    });

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }
        Array.from(event.target.files).forEach((file) => {
            // console.log(file.size);
            if (maxMbSize !== undefined && file.size > maxMbSize * 1000000) {
                // console.log(maxMbSize);
                enqueueSnackbar(<Typography>Fichier trop lourd</Typography>, {
                    variant: 'error',
                });
                throw new Error('Fichier trop lourd');
            }
            // console.log(file.type);
            if (accept !== undefined && !accept.split(',').includes(file.type)) {
                // console.log(accept);
                enqueueSnackbar(<Typography>Format de fichier non compatible</Typography>, {
                    variant: 'error',
                });
                throw new Error('Format de fichier non compatible');
            }
        });
        onChange(event);
    };

    const handleMouseEnter = () => setIsHover((val) => val + 1);
    const handleMouseLeave = () => setIsHover((val) => val - 1);

    const dragEvents = {
        onMouseEnter: (e: MouseEvent) => {
            handleMouseEnter();
            stopDefaults(e);
        },
        onMouseLeave: (e: MouseEvent) => {
            handleMouseLeave();
            stopDefaults(e);
        },
        onDragEnter: (e: DragEvent) => {
            handleMouseEnter();
            stopDefaults(e);
        },
        onDragLeave: (e: DragEvent) => {
            handleMouseLeave();
            stopDefaults(e);
        },
        onDragOver: stopDefaults,
        onDrop: (e: DragEvent<HTMLElement>) => {
            handleDropConstraints(e);
        },
    };

    return (
        <Box sx={{ width }}>
            <label
                htmlFor="file-upload"
                {...dragEvents}
            >
                <Box
                    height={height}
                    sx={
                        border || (hoverBorder && isHover > 0)
                            ? {
                                  border: 1,
                                  borderStyle: 'dashed',
                                  backgroundColor: 'text.gris005',
                                  borderColor: 'text.gris35',
                                  borderRadius: '12px',
                              }
                            : {}
                    }
                >
                    <Box
                        height="100%"
                        width="100%"
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {inline ? null : (
                            <Typography color="GrayText">
                                <FontAwesomeIcon
                                    icon={faCloudArrowDown}
                                    bounce={isHover > 0}
                                    size="3x"
                                />
                            </Typography>
                        )}
                        <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="center"
                        >
                            <Button
                                id="basic-button"
                                color="inherit"
                                component="label"
                            >
                                <Typography
                                    variant={inline ? 'l2dcaption' : 'l2dbodym'}
                                    color="primary.dark"
                                    sx={{
                                        fontWeight: 500,
                                    }}
                                >
                                    {btnText}
                                </Typography>
                                {maxMbSize !== undefined ? (
                                    <input
                                        type="file"
                                        onChange={handleChange}
                                        accept={accept}
                                        size={maxMbSize * 1000000}
                                        multiple
                                        hidden
                                    />
                                ) : (
                                    <input
                                        type="file"
                                        onChange={handleChange}
                                        accept={accept}
                                        multiple
                                        hidden
                                    />
                                )}
                                <Typography
                                    variant={inline ? 'l2dcaption' : 'l2dbodym'}
                                    sx={{
                                        marginLeft: '5px',
                                        fontWeight: 500,
                                    }}
                                >
                                    {dropText}
                                </Typography>
                            </Button>
                        </Box>
                        {inline || disableFormat ? null : (
                            <Typography
                                variant="caption"
                                color="GrayText"
                            >
                                Formats: {formatInfo}{' '}
                                {maxMbSize ? `- Poids maximum :${maxMbSize}Mo` : ''}
                            </Typography>
                        )}
                        {additionalInfo}
                    </Box>
                </Box>
            </label>
        </Box>
    );
};
